// @section 1 BD Opportunity kanban (2026-06-07 redesign).
//
// Cards = opportunities (one per hq_crm.deals row). Columns = the 6 BD
// workflow stages stored on deals.bd_stage:
//   Identification → Processing → Engagement → Offer Generation →
//   Negotiation → Closed (won / lost mixed in last column, color-tagged).
//
// Every opportunity ALWAYS carries 5 modules (Ask · Facts · Findings ·
// Pursuit · Offer). The counts strip on each card surfaces module richness
// at a glance. Click a card → /accounts/:companyId/bd (per-account BD lens
// with the module tabs).
//
// The legacy queue-item kanban moved to /api/hq/founder-os/bd-queue; that
// endpoint stays live for the operator-task surface (separate concern).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FoBdOpportunity,
  FoBdStageRollup,
  FoInitiative,
  FoTargeting,
  FoVip,
  BdWorkflowStage,
  foGetBdOpportunities,
  foGetCriticalInitiatives,
  foGetTargeting,
  foGetVips,
} from '../api';

// @subsection 1.1 Stage metadata

const BD_WORKFLOW_STAGES: BdWorkflowStage[] = [
  'identification', 'processing', 'engagement',
  'offer_generation', 'negotiation', 'closed_won',
];
// 'closed_lost' is rendered in the same final column as closed_won — Adam's
// 6-column model lumps both closed states together, color-tagged on the card.

const STAGE_LABEL: Record<BdWorkflowStage, string> = {
  identification:   'Identification',
  processing:       'Processing',
  engagement:       'Engagement',
  offer_generation: 'Offer Generation',
  negotiation:      'Negotiation',
  closed_won:       'Closed',
  closed_lost:      'Closed',
};

const STAGE_DOT: Record<BdWorkflowStage, string> = {
  identification:   'var(--tx-3)',
  processing:       'var(--info)',
  engagement:       'var(--brand-3)',
  offer_generation: 'var(--purple)',
  negotiation:      'var(--warn)',
  closed_won:       'var(--pos)',
  closed_lost:      'var(--neg)',
};

// @subsection 1.2 Tier chip per opportunity (Client / Prospect / Lost)
//   bd_stage closed_won → Client; closed_lost → Lost; everything else → Prospect.
const TIER = (s: BdWorkflowStage) => {
  if (s === 'closed_won')  return { label: 'Client',   bg: 'rgba(20,184,166,0.18)',  fg: 'var(--pos)' };
  if (s === 'closed_lost') return { label: 'Lost',     bg: 'rgba(255,255,255,0.06)', fg: 'var(--tx-4)' };
  return                          { label: 'Prospect', bg: 'rgba(96,165,250,0.14)',  fg: 'var(--info)' };
};

function fmtUsd(cents: number | null | undefined): string {
  if (cents == null) return '';
  const usd = cents / 100;
  if (usd >= 1_000_000) return `$${(usd / 1_000_000).toFixed(1)}M`;
  if (usd >= 1_000)     return `$${Math.round(usd / 1_000)}K`;
  return `$${usd.toFixed(0)}`;
}

// @section 2 Component

export default function Bd() {
  const nav = useNavigate();
  const [opps, setOpps] = useState<FoBdOpportunity[] | null>(null);
  const [stages, setStages] = useState<FoBdStageRollup[]>([]);
  const [initiatives, setInitiatives] = useState<FoInitiative[]>([]);
  const [vips, setVips] = useState<FoVip[]>([]);
  const [targeting, setTargeting] = useState<FoTargeting | null>(null);
  const [error, setError] = useState<string | null>(null);

  // @block 2.1 Refresh — opportunities + side-rail data in parallel.
  const refresh = useCallback(async () => {
    try {
      const [bd, inits, v, t] = await Promise.all([
        foGetBdOpportunities(),
        foGetCriticalInitiatives(),
        foGetVips(),
        foGetTargeting(),
      ]);
      setOpps(bd.rows);
      setStages(bd.stages);
      setInitiatives(inits);
      setVips(v);
      setTargeting(t);
    } catch (e) { setError(String(e)); }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  // @block 2.2 Derived: total counts for the top KPI strip.
  const totals = useMemo(() => {
    if (!opps) return { total: 0, clients: 0, prospects: 0, lost: 0, active_value: 0 };
    return {
      total:     opps.length,
      clients:   opps.filter(o => o.bd_stage === 'closed_won').length,
      lost:      opps.filter(o => o.bd_stage === 'closed_lost').length,
      prospects: opps.filter(o => o.bd_stage !== 'closed_won' && o.bd_stage !== 'closed_lost').length,
      active_value: opps
        .filter(o => o.bd_stage !== 'closed_lost')
        .reduce((sum, o) => sum + (o.value_cents ?? 0), 0),
    };
  }, [opps]);

  if (error) {
    return <div className="v5-page" style={{ padding: 24, color: 'var(--neg)' }}>API error: {error}</div>;
  }
  if (!opps) {
    return <div className="v5-page" style={{ padding: 24, color: 'var(--tx-3)' }}>Loading BD opportunities…</div>;
  }

  return (
    <div className="v5-page page-bd">
      <div className="bd-page-body">
        <div className="page-head" style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 0 }}>
          <div className="crumb"><span>Atlas</span><span className="sep">›</span><b>Business Development</b></div>
          <div className="page-title">
            <h1>BD Opportunities</h1>
            <div className="sub">
              {totals.total} opportunities · {totals.prospects} active prospects · {totals.clients} clients
              {totals.lost > 0 ? ` · ${totals.lost} closed-lost` : ''}
            </div>
          </div>
        </div>

        <div className="bd-strip">
          <div className="bd-stat lead">
            <div className="lbl">Active opportunity value</div>
            <div className="val">{fmtUsd(totals.active_value) || '—'}</div>
            <div className="delta">excludes closed-lost</div>
          </div>
          {BD_WORKFLOW_STAGES.map(stageKey => {
            const rollup = stages.find(s => s.bd_stage === stageKey);
            const isClosed = stageKey === 'closed_won';
            const lostRoll = isClosed ? stages.find(s => s.bd_stage === 'closed_lost') : null;
            const count = (rollup?.deal_count ?? 0) + (lostRoll?.deal_count ?? 0);
            return (
              <div className="bd-stat" key={stageKey}>
                <div className="lbl">{STAGE_LABEL[stageKey]}</div>
                <div className="val">{count}</div>
                <div className="delta">{fmtUsd((rollup?.value_cents ?? 0) + (lostRoll?.value_cents ?? 0))}</div>
              </div>
            );
          })}
        </div>

        {/* @block 2.3 6-column opportunity kanban */}
        <div className="pipeline-flow">
          {BD_WORKFLOW_STAGES.map(stageKey => {
            const colOpps = opps.filter(o =>
              o.bd_stage === stageKey
              || (stageKey === 'closed_won' && o.bd_stage === 'closed_lost')
            );
            return (
              <div className="stage-col" key={stageKey}>
                <div className="stage-head">
                  <span className="name">
                    <span className="indicator" style={{ background: STAGE_DOT[stageKey] }} />
                    {STAGE_LABEL[stageKey]}
                  </span>
                  <span className="count">{colOpps.length}</span>
                </div>
                <div className="stage-col-body">
                  {colOpps.map(o => {
                    const tier = TIER(o.bd_stage);
                    return (
                      <div
                        key={o.deal_id}
                        className="acct-card"
                        style={{ borderLeftColor: STAGE_DOT[o.bd_stage], cursor: o.company_id ? 'pointer' : 'default' }}
                        onClick={() => { if (o.company_id) nav(`/accounts/${o.company_id}/bd`); }}
                      >
                        {/* Company name leads (per Adam, "focus should be the company's name") */}
                        <div className="row1" style={{ alignItems: 'flex-start' }}>
                          <div className="name" style={{ flex: 1, minWidth: 0 }}>
                            {o.badge && <span style={{ opacity: 0.6, fontSize: 10, marginRight: 4 }}>{o.badge}</span>}
                            {o.company_name ?? '(unknown)'}
                          </div>
                          <span
                            style={{
                              fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
                              padding: '2px 6px', borderRadius: 4, textTransform: 'uppercase',
                              background: tier.bg, color: tier.fg, whiteSpace: 'nowrap',
                            }}
                          >
                            {tier.label}
                          </span>
                        </div>

                        {/* Opportunity name (the deal) — secondary line */}
                        <div className="meta" style={{ fontSize: 11, color: 'var(--tx-3)' }}>
                          {o.deal_name}
                        </div>

                        {/* The 5 always-present modules, counts at a glance */}
                        <div style={{ display: 'flex', gap: 10, fontSize: 10, color: 'var(--tx-4)', marginTop: 2, flexWrap: 'wrap' }}>
                          <span title="Asks (their stated need)"><b style={{ color: 'var(--tx-2)' }}>{o.asks_count}</b> ask</span>
                          <span title="Facts (what we learned)"><b style={{ color: 'var(--tx-2)' }}>{o.facts_count}</b> fact</span>
                          <span title="Findings (conclusions)"><b style={{ color: 'var(--tx-2)' }}>{o.findings_count}</b> find</span>
                          <span title="Pursuit (why pursue + upside)"><b style={{ color: 'var(--tx-2)' }}>{o.pursuits_count}</b> purs</span>
                          <span title="Offers"><b style={{ color: 'var(--tx-2)' }}>{o.offers_count}</b> off</span>
                        </div>

                        <div className="foot">
                          <div className="deal">{fmtUsd(o.value_cents) || '—'}</div>
                          <div className="decay" style={{ fontSize: 10, color: 'var(--tx-4)' }}>
                            {o.last_activity_at
                              ? new Date(o.last_activity_at).toLocaleDateString()
                              : (o.pipeline_stage ?? '·')}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {colOpps.length === 0 && (
                    <div style={{ padding: '12px 14px', color: 'var(--tx-4)', fontSize: 11, fontStyle: 'italic' }}>
                      empty
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <BdRail initiatives={initiatives} vips={vips} targeting={targeting} />
    </div>
  );
}

// @section 3 Right rail — Today's targeting, Critical Initiatives, VIPs.
// Carried over from the previous BD console; these widgets are general
// operator context, independent of the opportunity kanban shape.
function BdRail({
  initiatives, vips, targeting,
}: { initiatives: FoInitiative[]; vips: FoVip[]; targeting: FoTargeting | null }) {
  return (
    <div className="bd-rail">
      <div className="rail-card tier-1">
        <h3>Today's targeting <span className="ct">{1 + (targeting?.also.length ?? 0)}</span></h3>
        {targeting?.next_move ? (
          <>
            <div className="target-hero">
              <div className="now-lbl">Next move</div>
              <div className="title">{targeting.next_move.title}</div>
              <div className="meta">{targeting.next_move.next_action ?? `${targeting.next_move.priority} · ${targeting.next_move.progress_pct}%`}</div>
            </div>
            {targeting.also.map(a => (
              <div className="target-also" key={a.id}>
                <div className="lbl">Then</div>
                <div className="title">{a.title}</div>
                <div className="meta">{a.next_action ?? `${a.priority} · ${a.progress_pct}%`}</div>
              </div>
            ))}
          </>
        ) : (
          <div style={{ color: 'var(--tx-4)', fontSize: 12 }}>No open queue items.</div>
        )}
      </div>

      <div className="rail-card">
        <h3>Critical Initiatives <span className="ct">{initiatives.length}</span></h3>
        {initiatives.map(c => (
          <div className="crit-item" key={c.id}>
            <div className="ord">{c.ord}</div>
            <div className="body">
              <div className="title">
                {c.title}
                {c.status_badge && (
                  <span className={`badge${c.status_badge_kind ? ` ${c.status_badge_kind}` : ''}`}>
                    {c.status_badge}
                  </span>
                )}
              </div>
              {c.meta && <div className="meta">{c.meta}</div>}
            </div>
          </div>
        ))}
        {initiatives.length === 0 && (
          <div style={{ color: 'var(--tx-4)', fontSize: 12 }}>None pinned to founder-OS tier 1.</div>
        )}
      </div>

      <div className="rail-card">
        <h3>VIPs <span className="ct">{vips.length}</span></h3>
        <div className="vip-list">
          {vips.map(v => (
            <div className="vip-item" key={v.id}>
              <div className={`vip-ava${v.avatar_color ? ` ${v.avatar_color}` : ''}`}>{v.initials}</div>
              <div className="vip-info">
                <div className="vip-name">{v.full_name}</div>
                {v.role && <div className="vip-role">{v.role}</div>}
              </div>
              <div className={`vip-age${v.age_band && v.age_band !== 'fresh' ? ` ${v.age_band}` : ''}`}>
                <span className="dot"></span>{v.age_label}
              </div>
            </div>
          ))}
          {vips.length === 0 && (
            <div style={{ color: 'var(--tx-4)', fontSize: 12 }}>No VIPs pinned (set vip_order on contacts).</div>
          )}
        </div>
      </div>
    </div>
  );
}
